import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'expo-router'
import { useThemeColors } from '../../lib/useThemeColors'
import { colors, fonts } from '../../lib/theme'
import { CronoDisplay, SectionLabel, Skeleton, Carousel } from './ui'
import WalletStack, { MiniCountdown } from './WalletStack'
import GradientButton from '../GradientButton'
import { raceLogo } from '../../lib/raceLogos'

// Raggruppa le pick per gara: una card per gara, con la prossima tappa
// pickabile di quella gara e il conteggio delle altre tappe rimanenti
// nella STESSA gara (non nel totale cross-gara).
function groupByRace(picks) {
  const raceMap = {}
  const order = []
  // NIENTE sort per id_race: `picks` arriva già ordinato per
  // stage_date/id_stage_number da fn_stage_to_pick, quindi il primo
  // avvistamento di ogni gara nel loop è già nell'ordine cronologico
  // della sua prossima tappa pickabile — preservarlo (order.push, senza
  // riordinare dopo) basta a mostrare per prima la gara con la pick più
  // vicina nel tempo, non quella con id_race più basso.
  picks?.forEach((p) => {
    if (!raceMap[p.id_race]) {
      raceMap[p.id_race] = { ...p, restCount: 0 }
      order.push(p.id_race)
    } else {
      raceMap[p.id_race].restCount += 1
    }
  })
  return order.map((id) => raceMap[id])
}

function PickCard({ pick, onGo }) {
  const { t } = useTranslation()
  const router = useRouter()
  const styles = makeStyles(useThemeColors())
  const logo = raceLogo(pick.race_name)

  return (
    <View style={styles.accentCard}>
      {logo && <Image source={logo} style={styles.raceLogo} resizeMode="contain" pointerEvents="none" accessible={false} />}
      <Text style={styles.raceName}>
        {pick.id_type_race === 3
          ? pick.race_name
          : `${pick.race_name} — ${t('common.stage')} ${pick.stage}`}
      </Text>
      <CronoDisplay date={pick.stage_date} time={pick.stage_time} />
      {pick.has_picked && (
        <View style={{ paddingHorizontal: 16, paddingBottom: 4 }}>
          <Text style={styles.pickDone}>{t('pick.pick_done')}</Text>
        </View>
      )}
      <View style={styles.footer}>
        {pick.restCount > 0 ? (
          <TouchableOpacity onPress={() => router.push('/pick')}>
            <Text style={styles.otherPicks}>{t('home.other_picks', { count: pick.restCount })}</Text>
          </TouchableOpacity>
        ) : <View />}
        <GradientButton onPress={() => onGo(pick)} size="md">{t('common.go')} </GradientButton>
      </View>
    </View>
  )
}

export default function PickSection({ picks, onGo, loading }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = makeStyles(tc)

  if (loading) return (
    <View style={{ marginBottom: 24 }}>
      <SectionLabel>{t('home.section_picks')}</SectionLabel>
      <View style={[styles.card, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
        <Skeleton width="70%" height={18} style={{ marginBottom: 12 }} />
        <Skeleton width="50%" height={40} style={{ marginBottom: 12 }} />
        <Skeleton width="30%" height={32} style={{ alignSelf: 'flex-end' }} />
      </View>
    </View>
  )

  const races = groupByRace(picks)

  return (
    <View style={{ marginBottom: 24 }}>
      <View style={styles.labelRow}>
        <SectionLabel>{t('home.section_picks')}</SectionLabel>
        {races.length > 1 && (
          <View style={styles.countBadge}>
            <Text style={styles.countBadgeText}>{t('home.races_count', { count: races.length, defaultValue: `${races.length} corse` })}</Text>
          </View>
        )}
      </View>
      {races.length === 0 ? (
        <View style={[styles.emptyCard, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
          <Text style={{ color: tc.rosaText, fontSize: 18 }}>✓</Text>
          <Text style={[styles.emptyText, { color: tc.textMuted }]}>{t('home.no_races')}</Text>
        </View>
      ) : races.length > 1 ? (
        // Piu' gare da scegliere: mazzo stile Wallet, le card dietro mostrano nome gara e conto alla rovescia
        <WalletStack
          items={races.map((pick) => ({
            key: pick.id_race,
            label: pick.race_name,
            card: <PickCard pick={pick} onGo={onGo} />,
            strip: <MiniCountdown date={pick.stage_date} time={pick.stage_time} />,
          }))}
        />
      ) : (
        <Carousel>
          {races.map((pick) => (
            <PickCard key={pick.id_race} pick={pick} onGo={onGo} />
          ))}
        </Carousel>
      )}
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  labelRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  countBadge: { backgroundColor: tc.bgAccent ?? tc.rosaSubtle ?? 'rgba(232,57,125,0.12)', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 3 },
  countBadgeText: { fontSize: 12, fontWeight: '700', color: tc.text },
  card: { borderRadius: 14, borderWidth: 1, padding: 16 },
  accentCard: { backgroundColor: tc.bgCard, borderRadius: 14, borderWidth: 1, borderColor: tc.rosaBorder, overflow: 'hidden' },
  // logo gara in dissolvenza dietro il contenuto, tagliato dall'angolo in alto a destra, lontano dal conto alla rovescia
  raceLogo: { position: 'absolute', top: -100, right: -80, width: 240, height: 240, opacity: 0.1 },
  emptyCard: { flexDirection: 'row', alignItems: 'center', gap: 10, borderRadius: 14, borderWidth: 1, padding: 16 },
  emptyText: { fontSize: 14 },
  raceName: { fontSize: 20, fontFamily: fonts.condensedBold, color: tc.text, paddingHorizontal: 16, paddingTop: 4, paddingBottom: 4 },
  pickDone: { fontSize: 13, color: '#10B981', fontWeight: '600' },
  footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingBottom: 14, paddingTop: 4 },
  otherPicks: { fontSize: 13, color: tc.textMuted, textDecorationLine: 'underline' },
  goBtn: { backgroundColor: tc.rosa, borderRadius: 8, paddingHorizontal: 24, paddingVertical: 9 },
  goBtnText: { color: tc.brandText, fontFamily: fonts.condensedBold, fontSize: 14 },
})